/**
 * Detección del tipo de documento KYC a partir del NOMBRE del archivo — el motor
 * del arrastrar-y-soltar del registro: sueltas varios archivos y cada uno cae en
 * su ranura de la plantilla sin elegirlo a mano.
 *
 * Deliberadamente conservador (anti-falso-positivo, misma filosofía que el parser
 * de RP): coincide frases completas con límite de palabra sobre el nombre
 * normalizado (minúsculas, sin acentos, separadores → espacio); ante ambigüedad
 * gana la frase más larga (más específica) y sin ninguna coincidencia devuelve
 * `null` — la UI lo deja en "sin clasificar" para asignación manual.
 *
 * Solo nombre de archivo, no contenido: es puro, corre en el navegador al instante
 * y no requiere OCR. // ponytail: clasificación por contenido (OCR kind) cuando el
 * endpoint ocr-prefill soporte más `kind`s que 'id'.
 */
import { DOC_TYPE_KEYS } from './required-docs.js';
/** Frases (ya normalizadas: minúsculas, sin acentos) que identifican cada tipo. */
const DOC_DETECT_KEYWORDS = {
    cedula_pasaporte: ['cedula', 'pasaporte', 'passport', 'identidad', 'dni', 'id card'],
    comprobante_domicilio: [
        'domicilio',
        'recibo de luz',
        'recibo de agua',
        'recibo luz',
        'recibo agua',
        'utility bill',
        'proof of address',
    ],
    carta_referencia_bancaria: ['referencia bancaria', 'ref bancaria', 'carta bancaria', 'bank reference'],
    carta_referencia_profesional: [
        'referencia profesional',
        'referencia comercial',
        'ref profesional',
        'referencia laboral',
    ],
    declaracion_pep: ['pep', 'persona expuesta politicamente', 'persona expuesta'],
    fuente_fondos: ['fuente de fondos', 'fuente fondos', 'origen de fondos', 'origen fondos', 'source of funds'],
    pacto_social: ['pacto social', 'articles of incorporation', 'escritura de constitucion', 'escritura constitucion'],
    certificado_registro_publico: [
        'registro publico',
        'certificado de registro',
        'certificacion registral',
        'good standing',
    ],
    ruc_aviso_operacion: ['ruc', 'aviso de operacion', 'aviso operacion'],
    registro_accionistas: [
        'registro de accionistas',
        'registro accionistas',
        'libro de accionistas',
        'libro accionistas',
        'shareholders register',
    ],
    poder_acta_junta: ['poder', 'acta de junta', 'acta junta', 'power of attorney', 'board resolution'],
    organigrama_estructura: ['organigrama', 'estructura de propiedad', 'ownership chart'],
    estados_financieros: [
        'estados financieros',
        'estado financiero',
        'balance general',
        'financial statements',
        'balance',
    ],
    formulario_kyc_firmado: ['kyc', 'conozca a su cliente', 'debida diligencia', 'ddc'],
    certificados_acciones: [
        'certificado de acciones',
        'certificados de acciones',
        'certificado acciones',
        'stock certificate',
    ],
    acta_fundacional: ['acta fundacional', 'carta fundacional', 'foundation charter'],
    declaracion_jurada_bf: ['beneficiario final', 'beneficiario', 'declaracion jurada', 'beneficial owner', 'ubo'],
    fatca_crs: ['fatca', 'crs', 'w8', 'w9', 'w 8', 'w 9', 'w8ben', 'w9ben', '8ben', 'autocertificacion'],
    perfil_idoneidad: ['idoneidad', 'suitability', 'perfil del inversionista', 'perfil inversionista'],
    datos_poliza: ['poliza'],
    datos_inmueble: ['inmueble', 'finca', 'promesa de compraventa', 'compraventa'],
    forma_pago: ['forma de pago', 'forma pago', 'comprobante de pago', 'transferencia'],
};
/** minúsculas, sin acentos, todo separador/símbolo → un espacio. */
function normalize(s) {
    return s
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
/**
 * Detecta el tipo de documento por el nombre del archivo. `candidates` acota la
 * búsqueda (p.ej. primero las ranuras que faltan); sin coincidencia → `null`.
 */
export function detectDocType(fileName, candidates) {
    const hay = normalize(fileName);
    if (hay === '')
        return null;
    const pool = candidates && candidates.length > 0 ? candidates : DOC_TYPE_KEYS;
    let best = null;
    let bestScore = 0;
    for (const key of pool) {
        for (const kw of DOC_DETECT_KEYWORDS[key]) {
            // Frase completa con límite de palabra — 'poder' no coincide en 'apoderado'.
            if (!new RegExp(`(^|\\s)${escapeRe(kw)}(\\s|$)`).test(hay))
                continue;
            if (kw.length > bestScore) {
                bestScore = kw.length;
                best = key;
            }
        }
    }
    return best;
}
//# sourceMappingURL=doc-detect.js.map